import { useState } from "react";
import { useQuery, useMutation, queryOptions } from "@tanstack/react-query";

import { getMogrtStatus, renderMogrtTemplate } from "@app/services/mogrt";

import { listFilesInDirectory } from "@app/services/s3";

import { getJobStatus } from "@app/services/jobs";

import { getAlloy } from "@app/utils/analytics";

export const mogrtTemplateKey = {
  all: ["mogrtTemplates"],
  lists: () => [...mogrtTemplateKey.all, "list"],
  describes: () => [...mogrtTemplateKey.all, "describes"],
  describe: (id) => [...mogrtTemplateKey.describes(), id],
  voices: () => [...mogrtTemplateKey.all, "voices"],
  statuses: () => [...mogrtTemplateKey.all, "statuses"],
  status: (jobId) => [...mogrtTemplateKey.statuses(), jobId],
};

async function describeSingleMogrtPath(path, signal) {
  const directoryPath = `${path}/`;
  const mediaDirectoryPath = `${path}/media/`;
  const videoDirectoryPath = `${path}/videos/`;
  const eprDirectoryPath = `${path}/epr/`;
  const musicDirectoryPath = `${path}/music/`;

  const [
    mediaResponse,
    videoResponse,
    eprResponse,
    fileResponse,
    musicResponse,
  ] = await Promise.all([
    listFilesInDirectory(mediaDirectoryPath, { signal }),
    listFilesInDirectory(videoDirectoryPath, { signal }),
    listFilesInDirectory(eprDirectoryPath, { signal }),
    listFilesInDirectory(directoryPath, { signal }),
    listFilesInDirectory(musicDirectoryPath, { signal }),
  ]);

  const mediaItems = [
    ...(mediaResponse?.urls ?? []),
    ...(videoResponse?.urls ?? []),
  ];
  const mediaFiles = mediaItems
    .map((item) => item.filePath)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  const eprItems = eprResponse?.urls ?? [];
  const eprFiles = eprItems
    .map((item) => item.filePath)
    .filter((filePath) => filePath.toLowerCase().endsWith(".epr"));
  const eprPath = eprFiles?.[0] ?? "";

  const musicItems = musicResponse?.urls ?? [];
  const musicFiles = musicItems
    .map((item) => item.filePath)
    .filter(
      (filePath) =>
        filePath.toLowerCase().endsWith(".mp3") ||
        filePath.toLowerCase().endsWith(".wav"),
    );

  const isVoBed = (filePath) => /_vo\.(mp3|wav)$/i.test(filePath);
  const isNoVo = (filePath) => /_novo\.(mp3|wav)$/i.test(filePath);
  const musicPath =
    musicFiles.find(isVoBed) ??
    musicFiles.find((filePath) => !isNoVo(filePath)) ??
    musicFiles?.[0] ??
    "";
  const musicNoVoPath = musicFiles.find(isNoVo) ?? musicPath;

  const files = fileResponse?.urls ?? [];

  const mogrtFiles = files
    .map((item) => item.filePath)
    .filter((filePath) => filePath.toLowerCase().endsWith(".mogrt"));
  const mogrtPath = mogrtFiles?.[0];

  if (!mogrtPath) {
    throw new Error("No .mogrt file found in the selected folder");
  }

  const describeFile = files.find((item) =>
    item?.filePath?.toLowerCase().endsWith("describe-ga.json"),
  );

  if (!describeFile?.presignURL) {
    throw new Error("No describe.json found in the selected folder");
  }

  const describeResponse = await fetch(describeFile.presignURL, { signal });
  const describePayload = await describeResponse.json();
  const normalizedDescribe = describePayload?.output
    ? describePayload
    : { output: describePayload };
  const elements = normalizedDescribe?.output?.elements ?? [];
  const fonts = normalizedDescribe?.output?.fonts ?? [];
  const controls = elements.flatMap((element) => element?.controls ?? []);
  const formValues = controls.reduce((acc, control) => {
    if (control?.type === "text") {
      acc[control.id] = control?.defaultData?.text ?? "";
    } else if (control?.type === "media") {
      acc[control.id] = "";
    }
    return acc;
  }, {});

  return {
    mediaFiles,
    eprPath,
    musicPath,
    musicNoVoPath,
    mogrtPath,
    elements,
    fonts,
    controls,
    formValues,
  };
}

export const mogrtTemplateQueryOptions = (path, options = {}) =>
  queryOptions({
    queryKey: mogrtTemplateKey.describe(path),
    queryFn: ({ signal }) => describeSingleMogrtPath(path, signal),
    enabled: !!path,
    meta: { errorMessage: "Failed to fetch template data. Please try again." },
    ...options,
  });

export const modularTemplateQueryOptions = (templateModules, options = {}) => {
  const moduleList = Array.isArray(templateModules) ? templateModules : [];

  return queryOptions({
    queryKey: mogrtTemplateKey.describe(
      `modular:${moduleList.map((m) => m.rootPath).join("|")}`,
    ),
    queryFn: async ({ signal }) => {
      if (!moduleList.length) {
        throw new Error("No modules defined for the modular template");
      }

      const describeByPath = new Map();
      const describePath = (rootPath) => {
        if (!describeByPath.has(rootPath)) {
          describeByPath.set(
            rootPath,
            describeSingleMogrtPath(rootPath, signal),
          );
        }
        return describeByPath.get(rootPath);
      };

      const modules = await Promise.all(
        moduleList.map(async (m) => ({
          name: m.name,
          ...(await describePath(m.rootPath)),
        })),
      );

      return { modules };
    },
    enabled: moduleList.length > 0,
    staleTime: 1000 * 60 * 20, // 20 minutes
    gcTime: 1000 * 60 * 50, // 50 minutes
    meta: {
      errorMessage: "Failed to fetch modular template data. Please try again.",
    },
    ...options,
  });
};

// Job statuses at which a render has produced its output and polling can stop.
const TERMINAL_JOB_STATUSES = ["generated", "completed", "succeeded", "failed"];

export const jobStatusQueryOptions = (jobId, options = {}) =>
  queryOptions({
    queryKey: mogrtTemplateKey.status(jobId),
    queryFn: ({ signal }) => getJobStatus(jobId, { signal }),
    enabled: !!jobId,
    refetchInterval: (query) =>
      TERMINAL_JOB_STATUSES.includes(query.state.data?.status) ? false : 10000,
    ...options,
  });

export const useJobStatusQuery = (jobId, options = {}) => {
  return useQuery(jobStatusQueryOptions(jobId, options));
};

export const useDescribeMogrtTemplate = (path, options = {}) => {
  return useQuery(mogrtTemplateQueryOptions(path, options));
};

export const useDescribeModularTemplate = (templateModules, options = {}) => {
  return useQuery(modularTemplateQueryOptions(templateModules, options));
};

export const useJobStatus = (
  options = {},
  { onStatus, jobSuccessStatuses = ["completed"] } = {},
  jobFailureStatuses = ["failed"],
) => {
  return useMutation({
    mutationFn: async (variables, { signal }) => {
      const { jobId } = variables;
      const jobRecord = await getJobStatus(jobId, {
        signal: variables.signal ?? signal,
      });
      const status = jobRecord?.status;

      if (jobSuccessStatuses.includes(status)) {
        return jobRecord;
      } else if (status === "failed") {
        throw new Error("Job failed", {
          cause: jobRecord,
        });
      }

      onStatus?.(jobRecord);

      throw new Error("Job in progress", {
        cause: jobRecord,
      });
    },
    retryDelay: 10000, // Poll every 10 seconds
    retry: (failureCount, error) => {
      const status = error?.cause?.status;
      if (jobFailureStatuses.includes(status)) {
        return false;
      }
      return true;
    },
    meta: {
      errorMessage: "Render job failed. Please try again.",
    },
    ...options,
  });
};

export const useMogrtStatus = (
  options = {},
  { onStatus, getStatusFn = getMogrtStatus } = {},
) => {
  return useMutation({
    mutationFn: async (variables, { signal }) => {
      const { jobId } = variables;
      const statusResponse = await getStatusFn(jobId, {
        signal: variables.signal ?? signal,
      });
      const status = statusResponse?.status;

      if (status === "succeeded") {
        return statusResponse;
      } else if (status === "failed") {
        throw new Error("Mogrt rendering failed");
      }

      onStatus?.(statusResponse);

      throw new Error("Mogrt rendering in progress", {
        cause: statusResponse,
      });
    },
    retryDelay: 5000, // Poll every 5 seconds
    retry: (failureCount, error) => {
      if (error?.message?.toLowerCase()?.includes("failed")) {
        return false;
      }
      return true;
    },
    meta: {
      errorMessage: "Failed to fetch job status. Please try again.",
    },
    ...options,
  });
};

export const useRenderMogrtTemplate = (options = {}) => {
  const { jobSuccessStatuses, ...mutationOptions } = options ?? {};
  const [progress, setProgress] = useState(null);

  const jobStatus = useJobStatus(
    {
      onSettled: () => {
        setProgress(null);
      },
    },
    {
      onStatus: (result) => {
        if (result?.percentCompleted) {
          const percentCompleted = parseInt(result.percentCompleted, 10);
          setProgress((prev) => {
            if (prev === null) {
              return percentCompleted;
            }
            return Math.min(Math.max(prev, percentCompleted), 99);
          });
        }
      },
      ...(jobSuccessStatuses != null ? { jobSuccessStatuses } : {}),
    },
  );

  const mutation = useMutation({
    mutationFn: async (variables, { signal }) => {
      let jobId;
      let renderProgressInterval = null;

      if (variables?.existingJobId) {
        jobId = variables.existingJobId;
      } else {
        const payload = buildRenderPayload(variables);

        const identity = await getAlloy()?.("getIdentity", {
          namespaces: ["ECID"],
        });

        const renderResponse = await renderMogrtTemplate(
          {
            ...payload,
            videoId: variables.videoId,
            ecid: identity?.identity?.ECID,
          },
          { signal },
        );

        jobId = renderResponse?.jobId;

        if (!jobId) {
          throw new Error("Failed to render template. Please try again.", {
            cause: renderResponse,
          });
        }

        renderProgressInterval = setInterval(
          () => {
            setProgress((prev) => {
              if (prev === null) {
                return prev;
              } else {
                return Math.min(prev + 1, 99);
              }
            });
          },
          (10 * 60 * 1000) / 40,
        ); // 40 increments over 10 minutes
      }

      try {
        const jobRecord = await jobStatus.mutateAsync({
          jobId,
          signal,
        });

        const outputUrl = jobRecord?.outputs?.[0]?.destination?.url;

        if (!outputUrl) {
          throw new Error("Render succeeded but no output URL returned", {
            cause: { ...jobRecord, status: "failed" },
          });
        }

        return outputUrl;
      } finally {
        if (renderProgressInterval) {
          clearInterval(renderProgressInterval);
        }
      }
    },
    retry: false,
    meta: {
      errorMessage: "Failed to render template. Please try again.",
    },
    ...mutationOptions,
  });

  return {
    ...mutation,
    progress,
  };
};

// FIXME: temp limit
const MAX_MOGRT_MEDIA_ASSETS = 28;

const isBannerControl = (control) => /banner/i.test(control?.label ?? "");

export function buildRenderPayload({
  fonts,
  elements,
  formValues,
  mogrtPath,
  eprPath,
  musicPath,
  audioVariations,
}) {

  const isSingleAudioVariation = (audioVariations?.length ?? 0) <= 1;
  const audioPreference = isSingleAudioVariation
    ? "mix"
    : musicPath
      ? "replace"
      : "mix";
  const resolvedFonts = fonts
    .map((font) => font?.name)
    .filter(Boolean)
    .map((name) => {
      const fileName = name.toLowerCase().endsWith(".otf")
        ? name
        : `${name}.otf`;
      return {
        name,
        source: {
          path: `mogrt/fonts/${fileName}`,
        },
      };
    });
  const renderElements = elements.map((element) => {
    const controls = element.controls ?? [];

    const producesAsset = (control) =>
      (control?.type === "media" || control?.type === "audio") &&
      Boolean(formValues[control.variableId]);
    const assetControls = controls.filter(producesAsset);
    const excess = assetControls.length - MAX_MOGRT_MEDIA_ASSETS;
    const bannerControls = assetControls.filter(isBannerControl);
    const droppedBanners = new Set();
      // excess > 0
      //   ? new Set(bannerControls.slice(Math.max(0, bannerControls.length - excess)))
      //   : new Set();

    let assets = [];
    let variables = controls
      .filter((control) => {
        if (control?.type === "audio") {
          return Boolean(formValues[control.variableId]);
        }
        if (control?.type === "media") {
          if (!Boolean(formValues[control.variableId])) {
            return false;
          }
          if (droppedBanners.has(control)) {
            return false;
          }
          return true;
        }
        return true;
      })
      .map((control) => {
        if (control?.type === "text") {
          const text =
            formValues[control.variableId] ?? control?.defaultData?.text ?? "";
          return {
            variableId: control.variableId,
            text: text === "" ? " " : text,
          };
        }

        if (control?.type === "media") {
          const assetIndex =
            assets.push({
              source: formValues[control.variableId] ?? {},
            }) - 1;
          const label = control?.label?.toLowerCase() ?? "";
          return {
            variableId: control.variableId,
            assetIndex,
            scale:
              label.includes("logo") || label.includes("icon")
                ? "fit_to_frame"
                : "fill_frame",
          };
        }

        if (control?.type === "dropdown") {
          return {
            variableId: control.variableId,
            selectedDropdownValue:
              formValues[control.variableId] ??
              control.defaultData?.selectedDropdownValue ??
              "0",
          };
        }

        if (control?.type === "slider") {
          const range = control?.range ?? {};
          return {
            variableId: control.variableId,
            selectedSliderValue:
              formValues[control.variableId] ??
              control?.defaultData?.selectedSliderValue ??
              range?.minimum ??
              0,
          };
        }

        if (control?.type === "audio") {
          const assetIndex =
            assets.push({
              source: formValues[control.variableId] ?? {},
            }) - 1;
          return {
            variableId: control.variableId,
            assetIndex,
            audioPreference,
          };
        }

        return {
          variableId: control.variableId,
        };
      });

    return {
      variations: [{ variables }],
      assets,
    };
  });
  const renderElement = renderElements[0] ?? {
    variations: [{ variables: [] }],
    assets: [],
  };

  return {
    type: "mogrt",
    source: {
      path: mogrtPath,
    },
    ...(resolvedFonts.length > 0 ? { fonts: resolvedFonts } : {}),
    ...(eprPath
      ? {
          presets: [
            {
              source: {
                id: "custom_epr_ucx",
                path: eprPath,
              },
            },
          ],
        }
      : {
          presets: [
            {
              source: {
                presetId: "ffs_video_api_land_1080p_hq",
              },
            },
          ],
        }),
    ...renderElement,
    outputs: [
      {
        variationIndex: 0,
        presetIndex: 0,
        fileName: "output_0_0",
      },
    ],
  };
}
